import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { useSession } from "./api/auth-client";
import { ErrorBoundary } from "./components/error-boundary";
import { Dashboard } from "./routes/dashboard";
import { DeskDetail } from "./routes/desk-detail";
import { Login } from "./routes/login";

/**
 * Top-level auth guard.
 *
 * Three states:
 *   1. session loading       → render a tiny placeholder. Better Auth's
 *      cookie-cache makes this a few-ms blip in the hot path; we still
 *      handle it because the very first navigation has no cache.
 *   2. session present       → render the route. Always render Login if
 *      the user explicitly navigated there (e.g. testing sign-out).
 *   3. session absent on a   → push to /login. Done as an effect rather
 *      protected route          than navigate-during-render so React's
 *                               StrictMode double-invocation stays sane.
 *
 * Open paths (no auth required): /login. Health endpoints aren't routes,
 * they're API paths handled by the orchestrator directly.
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  const onLoginRoute = location.pathname === "/login";

  useEffect(() => {
    if (isPending) return;
    if (!session && !onLoginRoute) {
      navigate({ to: "/login", replace: true });
    } else if (session && onLoginRoute) {
      navigate({ to: "/", replace: true });
    }
  }, [session, isPending, onLoginRoute, navigate]);

  if (isPending) {
    return (
      <div
        className="h-full flex items-center justify-center"
        style={{
          background: "var(--bg)",
          color: "var(--t4)",
          fontFamily: "var(--font-ui)",
          fontSize: "var(--text-sm)",
        }}
      >
        Loading…
      </div>
    );
  }

  // Brief render-with-redirect-pending state. Returning null avoids a
  // flash of the dashboard shell behind the navigation.
  if (!session && !onLoginRoute) return null;
  if (session && onLoginRoute) return null;

  return <>{children}</>;
}

const rootRoute = createRootRoute({
  component: () => (
    <div className="h-full">
      <ErrorBoundary scope="root" showHomeLink>
        <AuthGate>
          <Outlet />
        </AuthGate>
      </ErrorBoundary>
    </div>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => (
    <ErrorBoundary scope="dashboard">
      <Dashboard />
    </ErrorBoundary>
  ),
});

const deskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/desk/$deskId",
  component: () => (
    <ErrorBoundary scope="desk-detail" showHomeLink>
      <DeskDetail />
    </ErrorBoundary>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => (
    <ErrorBoundary scope="login">
      <Login />
    </ErrorBoundary>
  ),
});

const routeTree = rootRoute.addChildren([indexRoute, deskRoute, loginRoute]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
